import {limit} from '@angular/fire/firestore';
import {
  Exclude,
  Expose, plainToClass,
  plainToClassFromExist,
  plainToInstance, Transform,
  Type
} from 'class-transformer';

import {Socket} from '../../classes/socket';
import {EntityArray} from '../../core/entityArray';
import {Helper} from '../../helpers/helper';
import {
  IGroupFullData,
  IGroupFullDataCounter,
  IMessageRequestChangeResponse
} from '../../interfaces/socket.interface';
import {LookinChatSDK} from '../../lib/lookinChatSDK';
import {CaseMetadata} from '../caseMetadata';
import {Message} from '../messages/message';
import {MessageContent} from '../messages/messageContent';
import {MessageRequest} from '../messages/messageRequest';
import {MessageState} from '../messages/messageState';
import {Messages} from '../messages/messages';
import {Pagination} from '../pagination';

import {GroupContent, PinMessagesCollection} from './groupContent';
import {GroupContentItem, GroupContentItemType} from './groupContentItem';
import {GroupDynamicData} from './groupDynamicData';
import {GroupRequest} from './groupRequest';
import {GroupState, GroupStateType} from './groupState';
import {GroupUser} from './groupUser';
import {GroupUserPermissions} from './groupUserPermissions';
import {File} from '../file';


/**
 * Single Group entity<br>
 * Contain all method to handle this group and his messages
 *
 * @category Group Entity
 * */
@Exclude()
export class Group {

  /**
   * Main id of group, can be null if was just created and wasn't created on server yet
   * */
  @Expose()
  id: string = null;

  /**
   * Group type<br>
   * `private` - if {@link caseMetadata} contains only two worker<br>
   * `public` - for all other cases
   * */
  @Expose()
  type: GroupType = 'public';

  /**
   * Group status<br>
   * `sending` - group was created and sent to server,<br>
   * `sent` - group was saved on server,<br>
   * `deleted` - group was deleted ( will be deleted when server will finish request )<br>
   * */
  @Expose()
  status: GroupStatus = 'sending';

  /**
   * Works as primary id while group wasn't saved on server
   * */
  @Expose()
  clientId: string;

  /**
   * Case metadata for this group, null if was created without it
   * */
  @Expose()
  @Type(() => CaseMetadata)
  caseMetadata: CaseMetadata = null;

  /**
   * Group users
   * */
  @Expose()
  @Type(() => GroupUser)
  @Transform(({value}) => new GroupUsersCollection(...value))
  users: GroupUsersCollection = new GroupUsersCollection();

  /**
   * Group state collection<br>
   * Each state is somehow related to change of this group:<br>
   * - if it was created, by who and when<br>
   * - if it was edited, by who and when<br>
   * so on...<br>
   * */
  @Expose()
  @Type(() => GroupState)
  state: GroupState[] = [];

  /**
   * Group content
   * */
  @Expose()
  @Type(() => GroupContent)
  content: GroupContent = new GroupContent();

  /**
   * Group dynamic data
   * */
  @Expose()
  @Type(() => GroupDynamicData)
  dynamicData: GroupDynamicData = new GroupDynamicData();

  /**
   * Messages for this group, populated by {@link getMessages}<br>
   * First items in array, newest, last - older<br>
   * If old ( history ) messages, will be requested, array will be removed and populated again<br>
   * <br>
   * Is a messages collection Class ( awesome right?, will change all array for this type of collections ),
   * */
  public messages: Messages = new Messages();

  /**
   * Scroll type:<br>
   * `recent` - most recent message is in the list, new messages will be added to this group<br>
   * `history` - this status will be used if we requested messages from some id, and there is still new messages that can be loaded, so most resent not in the list<br>
   * New messages will be not added ti list of messages, entered by user, or retrieved from server<br>
   * */
  public scrollType: GroupScrollType = 'recent';

  /**
   * If there is messages that can be loaded on top ( older )
   * */
  public canLoadUp = false;

  /**
   * If there is messages that can be loaded on bottom ( newer )
   * */
  public canLoadDown = false;

  /**
   * Connection status, when selecting group {@link select}
   * */
  public connectionStatus: GroupConnectionStatus = 'waiting';

  /**
   * If request on getting messages on going
   * */
  public messagesLoading = false;

  /**
   * If group selected
   * */
  public get selected() {
    return this.connectionStatus === 'ready';
  }

  /**
   * If group is ready, saved to database
   * */
  public get ready() {
    return this.status === 'active' || this.status === 'sent';
  }

  /**
   * Get title of group, handle `private` and `public` {@link type}
   * */
  public getTitle() {
    return this.type === 'private' ? this.getTitleFromUsers() : this.content.title ? this.content.title : 'Без заголовка';
  }

  /**
   * Get logo of group, handle `private` and `public` {@link type}
   * */
  public getLogo() {
    return this.type === 'private' ? this.getPhotoFromUsers() : this.content.logo;
  }

  /**
   * Only for internal needs
   *
   * @ignore
   * @internal
   * */
  constructor(content: GroupContent, state: GroupState, clientId: string = null) {
    this.content = content;
    this.state.push(state);

    this.clientId = clientId === null ? Helper.getRandomClientId(LookinChatSDK.user.id.toString()) : clientId;
  }

  /**
   * Event that will be called, when group entity changed<br>
   * {@link registerOnGroupUpdate}
   *
   * @event
   * @return array of change types {@link GroupStateType}
   * */
  private onGroupUpdate: (type: GroupStateType[]) => void = null;

  /**
   * Event that will be called, when messages in this group get changed<br>
   * {@link registerOnMessagesUpdate}
   *
   * @event
   * @return list of changed messages with their change types {@link MessageStateType}
   * */
  private onMessagesUpdate: (items: IMessageRequestChangeResponse[]) => void = null;

  /**
   * Select group<br>
   * Will load initial messages with two scenarios:<br><br>
   * 1. If Group has unread message, it will load messages starting from this first unread message, with `both` {@link GroupMessagesDirection}, {@link scrollType} will be set according to rules<br>
   * 2. If group has no unread, it will just load first XX messages<br><br>
   * Not ready group cant be selected
   *
   * @param deselectOthers - if we should deselect all other groups.
   *
   * @return Promise, resolve if all ok, throw error in case of error.
   * */
  public select(deselectOthers = false) {
    return new Promise<void>((resolve, reject) => {
      // if (!this.users.getCurrentUser().permissions.canReadMessages()) {
      //   resolve();
      //   return;
      // }

      if (this.selected) {
        resolve();
        return;
      }

      if (this.connectionStatus !== 'waiting') {
        resolve();
        return;
      }

      if (deselectOthers) {
        LookinChatSDK.instance.groups.deselectAllGroups()
      }

      this.changeConnectionStatus('connecting')

      // this.messages.groupId = this.id;
      this.messages = new Messages()
      this.scrollType = 'history';

      this.subscribeToChanges();
      this.changeConnectionStatus('loadingMessages')

      this.getInitialMessages().then(() => {
        this.changeConnectionStatus('ready')
        resolve()
      }).catch((error) => {
        reject(error);
      });
    });
  }

  /**
   * Will deselect group, change status to waiting and empty messages
   * */
  public unSelect() {
    this.messages = new Messages();
    this.changeConnectionStatus('waiting')
    this.unsubscribeToChanges();

    this.messagesLoading = false;
    this.onMessagesUpdate = null;
    this.onGroupUpdate = null;
  }

  /**
   * Will load older messages
   *
   * @return Promise, resolve if all ok ( or cant load more ), throw error in case of error.
   * */
  public loadMoreUp() {
    return new Promise<void>((resolve, reject) => {
      if (this.canLoadUp && !this.messagesLoading && this.messages.length) {
        this.getMessages('up', this.messages[this.messages.length - 1].id).then(() => {
          resolve()
        }).catch((error) => {
          reject(error)
        })
      } else {
        resolve()
      }
    });
  }

  /**
   * Will load newer messages
   *
   * @return Promise, resolve if all ok ( or cant load more ), throw error in case of error.
   * */
  public loadMoreDown() {
    return new Promise<void>((resolve, reject) => {
      if (this.canLoadDown && !this.messagesLoading && this.messages.length) {
        this.getMessages('down', this.messages[0].id).then(() => {
          resolve()
        }).catch((error) => {
          reject(error)
        })
      } else {
        resolve()
      }
    });
  }

  /**
   *  With this method we can load message for selected group.<br>
   *  It will put {@link messagesLoading} to `true` when loading in process and false when it is finished.<br>
   *  It will change {@link scrollType} also {@link canLoadUp} and {@link canLoadDown} according to answer<br>
   *  Duplicates will be removed automatically<br>
   *  Will call {@link onMessagesUpdate} after new messages will be added to array<br>
   *
   *  @param direction is to load newest or older messages, or both, null by default
   *  @param messageId id of message from which we should load, it will be loaded also with results
   *  @param limit how many, if we will set messageId and limit for example 20, we will get 21 messages, for both 41
   *  @param reset if we should clean messages array first. MUST can de used to load old messages.
   *
   * @return Promise, resolve if all ok ( or cant load more ), throw error in case of error.
   * */
  public getMessages(direction: GroupMessagesDirection = null, messageId: string = null, limit = 10, reset = false) {
    return new Promise<void>((resolve, reject) => {

      if (this.messagesLoading) {
        resolve();
        return;
      }

      this.messagesLoading = true;
      if (reset) {
        this.messages = new Messages();
      }

      Socket.instance.messageSocket.getMessages(this.id, direction, limit, 0, messageId).then((messages) => {
        // Socket.instance.messageSocket.getMessages('123', direction, limit, 0, messageId).then((messages) => {

        // @todo if we will request both and limit 50, we will not know if top or bottom has messages, need to think about it
        if (['both', 'up'].includes(direction)) {
          this.canLoadUp = messages.length >= limit;
        }

        if (['both', 'down'].includes(direction)) {
          this.canLoadDown = messages.length >= limit;
        }

        // @todo not sure if this needed
        if (direction === null && messageId === null) {
          this.canLoadUp = messages.length >= limit;
          this.canLoadDown = false;
        }

        this.scrollType = this.canLoadDown ? 'history' : 'recent';

        this.messagesLoading = false;
        this.insertOld(messages, direction);

        this.callOnMessagesUpdate();

        resolve();
      }).catch((error) => {

        LookinChatSDK.instance.callOnRejectUpdate(error.message);
        this.messagesLoading = false;
        reject(error)
      })
    });
  }

  /**
   * Getting full data for group,<br>
   * Currently it will return only counters for tabs.
   *
   * @return Promise, resolve with {@link IGroupFullDataCounter}, or throw error in case of error.
   * */
  public getFullData() {
    return new Promise<IGroupFullData>((resolve, reject) => {

      Socket.instance.groupSocket.getGroupFullData(this.id).then((data) => {
        resolve(data);
      }).catch((error) => {
        this.messagesLoading = false;
        reject(error)
      })
    });
  }

  /**
   * Getting full data for group,<br>
   * Currently it will return only counters for tabs.
   *
   * @param page - page number
   * @param contentType - content type
   *
   * @return Promise with {@link GroupContentItem} array and {@link Pagination} class, or throw error in case of error.
   * */
  public getGroupContent(page = 1, contentType: GroupContentItemType = null) {
    return Socket.instance.groupSocket.getGroupContent(page, 20, {
      groupId: this.id,
      contentType
    });
  }

  /**
   *  This will send request to edit message.<br>
   *  We can change something one, title for example, or whole content<br>
   *  Change will be applied immediately, and {@link onGroupUpdate} will be called<br>
   *  After server will handle request, group will be updated again and {@link onGroupUpdate} will be called again, also {@link LookinChatSDK.onGroupsUpdate} will be called<br>
   *  If group not ready, changes will be not made<br>
   *
   *  @param content partial of {@link GroupContent} null if no need to change
   *  @param users partial of {@link GroupUser} null if not need to change
   *  @param user partial of {@link GroupUser} null if not need to change
   *
   * @return Promise, resolve if all ok ( or cant edit ), throw error in case of error.
   * */
  public edit(content: Partial<GroupContent> = null, users: GroupUser[] = null, user: GroupUser = null) {
    return this.makeEdit(content, users, user)
  }

  /**
   *  This will send request to delete message.<br>
   *  New status `delete` will be applied immediately, and {@link onGroupUpdate} will be called<br>
   *  After server will handle request, group will be updated again and {@link onGroupUpdate} will be called again,<br>
   *  Group will be removed from array and {@link LookinChatSDK.onGroupsUpdate} will be called<br>
   *  If group not ready, changes will be not made<br>
   *
   * @return Promise, resolve if all ok ( or cant edit ), throw error in case of error.
   * */
  public delete() {
    return new Promise<void>((resolve, reject) => {
      if (this.ready) {
        const newState = new GroupState('delete')
        const request = new GroupRequest(['delete'], newState, this.id);

        this.status = 'deleted';
        this.callOnGroupUpdate(['delete']);

        // request.groupId = '123';

        Socket.instance.groupSocket.sendRequest(request).then((item) => {
          LookinChatSDK.instance.processGroupRequestEvent([item])

          resolve()
        }).catch((error) => {
          this.update(error.result.items[0].group, ['edit'])
          LookinChatSDK.instance.callOnGroupsUpdate(error.result.items);

          LookinChatSDK.instance.callOnRejectUpdate(error.message);
          reject(error);
        });

      } else {
        resolve()
      }
    });
  }

  /**
   *  Request to send new message.<br>
   *  If {@link scrollType} is `history`, message will not be added to array<br>
   *  If {@link scrollType} is `recent`, message will be added to array with {@link MessageStatus} is `sending`, {@link callOnUpdate} will be called<br>
   *  After server will save message, it will be returned and message will be updated, {@link callOnUpdate} will be called again<br>
   *  Event to update group list on chat will be sent from server to client<br>
   *  If group not ready or not selected, message will not send<br>
   *
   *  @param content partial of {@link MessageContent}
   *
   * @return Promise, resolve if all ok ( or cant send ), throw error in case of error.
   * */
  public sendMessage(content: Partial<MessageContent>) {
    return new Promise<void>((resolve, reject) => {
      if (this.selected && this.ready) {
        if (content.attachments?.length) {
          content.attachments = Helper.convertFiles(content.attachments);
        }

        const newContent = plainToInstance(MessageContent, content)
        const newState = new MessageState('create');
        const request = new MessageRequest(['create'], this.id, newState, newContent)

        let message: Message = null;
        if (this.scrollType === 'recent') {
          message = new Message(newContent, newState, this.id, request.clientId);
          this.messages.unshift(message);

          message.callOnUpdate(['create']);
        }

        // request.groupId = '123';

        Socket.instance.messageSocket.sendRequest(request).then((item) => {
          this.processMessageRequestEvent([item], true);
          resolve()
        }).catch((error) => {
          if (this.scrollType === 'recent') {
            message.status = 'error'
            message.callOnUpdate(['create']);
          }
          LookinChatSDK.instance.callOnRejectUpdate(error.message);
          reject(error);
        });
      } else {
        resolve()
      }
    });
  }

  /**
   * Should not be called outside
   *
   * @ignore
   * @internal
   * */
  public update(item: Group, type: GroupStateType[] = []) {
    this.content.logo = null;
    Object.assign(this, plainToClassFromExist(this, item));
    this.callOnGroupUpdate(type);
  }

  /**
   * Should not be called outside
   *
   * @ignore
   * @internal
   * */
  public callOnMessagesUpdate(items: IMessageRequestChangeResponse[] = []) {
    if (items.length) {
      this.messages.updateReplies(items);
    }

    if (this.onMessagesUpdate) {
      this.onMessagesUpdate(items);
    }
  }

  /**
   * Should not be called outside
   *
   * @ignore
   * @internal
   * */
  public callOnGroupUpdate(type: GroupStateType[] = []) {
    if (this.onGroupUpdate) {
      this.onGroupUpdate(type);
    }
  }

  /**
   * Will register anonymous function to change group entity event.
   *
   * @param callback - Anonymous function that will be called each time when group entity will be changed.
   * */
  public registerOnGroupUpdate(callback: (type: GroupStateType[]) => void) {
    this.onGroupUpdate = callback;
  }

  /**
   * Will register anonymous function to change messages event.
   *
   * @param callback - Anonymous function that will be called each time when any messages in this group get change.
   * */
  public registerOnMessagesUpdate(callback: (items: IMessageRequestChangeResponse[]) => void) {
    this.onMessagesUpdate = callback;
  }

  /**
   * Shortcut to add message to pinned of this group<br>
   * If message already pinned, nothing will be changed
   *
   * @param message to pin
   * @param removeOthers should we clean pins before add new or not
   * */
  public pinMessage(message: Message, removeOthers = false) {
    const existed = this.content.pinMessages.getById(message.id);

    if (removeOthers) {
      this.content.pinMessages = new PinMessagesCollection();
    }

    if (!existed) {
      this.content.pinMessages.push(message);
    }

    return this.edit({
      pinMessages: this.content.pinMessages
    })
  }

  /**
   * Shortcut to unpin message from this group
   *
   * @param message to unpin
   * */
  public unpinMessage(message: Message) {
    this.content.pinMessages.removeById(message.id)

    return this.edit({
      pinMessages: this.content.pinMessages
    })
  }

  /**
   * Request group leaving for current user
   * @todo when will add group user self editing, rewrite
   *
   * @return Promise, resolve if all ok ( or cant edit ), throw error in case of error.
   * */
  public leave() {
    // @todo when we will make edit user chat settings, use it
    const currentUser = this.users.getCurrentUser();
    currentUser.permissions = new GroupUserPermissions();

    return this.makeEdit(null, null, currentUser)
  }

  /**
   * Only for internal use, d`ont call from outside
   *
   * @ignore
   * @internal
   * */
  public processMessageRequestEvent(items: IMessageRequestChangeResponse[], useClientId = false) {

    items.forEach((item) => {
      const message = this.messages.getByField(useClientId ? 'clientId' : 'id', useClientId ? item.message.clientId : item.message.id);

      if (message) {
        message.update(item.message, item.type);
      } else {
        if (this.scrollType === 'recent') {
          this.messages.unshift(plainToInstance(Message, item.message))
        }
      }
    })

    this.callOnMessagesUpdate(items);
  }

  /**
   * Only for internal use, d`ont call from outside
   *
   * @ignore
   * @internal
   * */
  private makeEdit(content: Partial<GroupContent>, users: GroupUser[] = null, user: GroupUser = null) {
    return new Promise<void>((resolve, reject) => {
      if (this.ready) {
        if (content?.logo) {
          // @todo add validation
          content.logo = Helper.convertFile(content.logo);
        }

        const newState = new GroupState('edit')

        const newContent = content ? plainToClassFromExist(this.content, content) : null;
        const request = new GroupRequest(['edit'], newState, this.id);

        if (newContent) {
          request.data.content = newContent;
        }

        if (users !== null) {
          request.data.users = users;
          this.users = new GroupUsersCollection();
          this.users.push(...users);
        }

        if (user) {
          request.data.user = user;
        }
        this.state.push(newState);
        this.callOnGroupUpdate(['edit']);

        // request.groupId = '123';
        Socket.instance.groupSocket.sendRequest(request).then((item) => {
          console.log({item})
          // @todo user LookinChatSDK.instance.processGroupRequestEvent instead;
          this.update(item.group, ['edit'])
          LookinChatSDK.instance.callOnGroupsUpdate([item]);

          resolve()
        }).catch((error) => {
          console.log({error})
          this.update(error.result.items[0].group, ['edit'])
          LookinChatSDK.instance.callOnGroupsUpdate(error.result.items);

          LookinChatSDK.instance.callOnRejectUpdate(error.message);
          reject(error);
        });
      } else {
        resolve()
      }
    });
  }

  /**
   * @ignore
   * @internal
   * */
  private subscribeToChanges() {
    Socket.instance.messageSocket.subscribeToChanges(this.id, (items) => {
      this.processMessageRequestEvent(items);
    })
  }

  /**
   * @ignore
   * @internal
   * */
  private unsubscribeToChanges() {
    Socket.instance.messageSocket.unSubscribeFromChanges(this.id);
  }

  /**
   * In case if group is private, took title from his users
   * */
  private getTitleFromUsers() {
    return this.users.getInterlocutorUser()?.user?.getWorker()?.worker?.name || 'Группа между двумя сотрудниками';
  }

  /**
   * In case if group is private, took photo from his users
   * */
  private getPhotoFromUsers() {
    return this.users.getInterlocutorUser()?.user?.getWorker()?.worker?.photo;
  }

  /**
   * @ignore
   * @internal
   * */
  private changeConnectionStatus(newStatus: GroupConnectionStatus) {
    this.connectionStatus = newStatus;

    // @todo add callback if needed
  }

  /**
   * @ignore
   * @internal
   * */
  private getInitialMessages() {
    return new Promise<boolean>((resolve, reject) => {

      // this.dynamicData.firstUnreadMessageId = '64941a48083f3796a87e9a0c';

      if (this.dynamicData.firstUnreadMessageId) {
        this.scrollType = 'history';

        this.getMessages('both', this.dynamicData.firstUnreadMessageId).then(() => {
          resolve(true)
        }).catch((error) => {
          reject(error);
        })
      } else {
        this.getMessages().then(() => {
          resolve(true)
        }).catch((error) => {
          reject(error);
        })
      }
    });
  }

  /**
   * @ignore
   * @internal
   * */
  private insertOld(messages: Message[], direction: GroupMessagesDirection = null) {

    if (direction === 'down') {
      messages = messages.reverse();
    }

    messages.forEach((message) => {
      const m = this.messages.getById(message.id);

      if (m) {
        // @todo should we update it ... ?
      } else {

        if (direction === 'down') {
          this.messages.unshift(message);
        } else {
          this.messages.push(message);
        }

      }
    })
  }

}

/**
 * Group User collection
 * */
export class GroupUsersCollection extends EntityArray<GroupUser> {

  /**
   * Get worker from group users
   *
   * @param workerId
   *
   * @return found user group data
   * */
  getWorkerById(workerId: number | string) {
    return this.find(item => item.user.workerId.toString() === workerId.toString());
  }

  /**
   * Getting an interlocutor user in a chat
   *
   * @return interlocutor user group data
   * */
  public getInterlocutorUser() {
    return this.find(user => user.user.workerId.toString() !== LookinChatSDK.user.workerId.toString());
  }

  /**
   * Getting an interlocutor user in a chat
   *
   * @return interlocutor user group data
   * */
  public getCurrentUser() {
    return this.getWorkerById(LookinChatSDK.user.workerId);
  }

  /**
   * Getting the available users of a group
   *
   * @return available users group data
   * */
  public getAvailableUsers() {
    return this.filter(user => !!user.permissions.length && !user.user.getWorker()?.worker?.status?.isFired);
  }

  /**
   * Getting converted userId and workId to userId-workId
   *
   * @return converted user ids
   * */
  public getConvertedUsersIds() {
    return [...this.filter(user => user.permissions.length).map(user => user.user.id + '-' + user.user.workerId)];
  }
}

/**
 * Group status<br>
 * `sending` - group was created and sent to server,<br>
 * `sent` - group was saved on server,<br>
 * `deleted` - group was deleted ( will be deleted when server will finish request )<br>
 * */
export type GroupStatus = 'sending' | 'sent' | 'deleted' | 'active';

/**
 * Group type<br>
 * `private` - if {@link caseMetadata} contains only two worker<br>
 * `public` - for all other cases
 * */
export type GroupType = 'public' | 'private';

/**
 * GroupMessagesDirection<br>
 * To load newest or older messages, or both, null by default
 * */
export type GroupMessagesDirection = 'up' | 'down' | 'both';

/**
 * Scroll type:<br>
 * `recent` - most recent message is in the list, new messages will be added to this group<br>
 * `history` - this status will be used if we requested messages from some id, and there is still new messages that can be loaded, so most resent not in the list<br>
 * New messages will be not added ti list of messages, entered by user, or retrieved from server<br>
 * */
export type GroupScrollType = 'history' | 'recent';

/**
 * Connection status, when selecting group {@link select}
 * */
export type GroupConnectionStatus =
  'waiting'
  | 'connecting'
  | 'loadingMessages'
  | 'ready';
